import React, { useEffect, useRef } from 'react';
import { Box, Fab } from '@mui/material';
import { CameraAlt as CameraAltIcon } from '@mui/icons-material';

const CameraCapture = ({ open, onClose, onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    const start = async () => {
      // Require secure context for camera on most mobile browsers
      const isLocalhost = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
      const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
      if (!isSecure && !isLocalhost) {
        alert('Camera access requires HTTPS. Please use a secure connection.');
        onClose && onClose();
        return;
      }

      // Optional: preflight permission state (not supported on all browsers, e.g. iOS Safari)
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const status = await navigator.permissions.query({ name: 'camera' });
          // status.state can be 'granted' | 'prompt' | 'denied'
          if (status.state === 'denied') {
            alert('Camera permission is blocked. Please enable camera access in your browser settings and try again.');
            onClose && onClose();
            return;
          }
        }
      } catch (_) {
        // Ignore if not supported
      }

      try {
        // Try with stricter rear camera constraint first, then fall back
        const tryConstraints = async () => {
          const attempts = [
            { video: { facingMode: { exact: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false },
            { video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false },
            { video: true, audio: false },
          ];
          let lastErr;
          for (const constraint of attempts) {
            try {
              return await navigator.mediaDevices.getUserMedia(constraint);
            } catch (err) {
              lastErr = err;
            }
          }
          throw lastErr;
        };

        const stream = await tryConstraints();
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error('Error accessing camera:', err);
        const name = err?.name || '';
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          alert('Camera permission was denied. Please allow camera access in your browser settings and try again.');
        } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
          alert('No suitable camera was found on this device or the requested camera is unavailable.');
        } else {
          alert('Unable to access camera. Please check permissions and device settings.');
        }
        onClose && onClose();
      }
    };

    if (open) {
      start();
    }

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [open, onClose]);

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          onCapture && onCapture({ blob, url });
          onClose && onClose();
        }
      },
      'image/jpeg',
      0.8
    );
  };

  if (!open) return null;

  return (
    <Box sx={{ position: 'relative', textAlign: 'center' }}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        style={{ width: '100%', maxHeight: '400px', borderRadius: '8px' }}
      />
      <Fab
        color="primary"
        size="large"
        onClick={handleCapture}
        sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)' }}
      >
        <CameraAltIcon />
      </Fab>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Box>
  );
};

export default CameraCapture;
